import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faExclamationTriangle,
  faPen,
  faC,
  faDatabase
} from '@fortawesome/free-solid-svg-icons';
import { useHVAConversation } from './HVAFineTuningPlaygroundContext';
import Button from 'components/base/Button';
import { updateAgentSampleData } from 'services/hvaagent.service';
import HVACustomerSelectorModal from './HVACustomerSelectorModal';
import { RealCustomer } from 'services/hvaagent.service';

interface TestingDataFormProps {
  agentId: string;
  initialData?: {
    companyName?: string;
    customerName?: string;
    importId?: string;
    recordId?: string;
    customerPhone?: string;
    customerTrn?: string;
    customerReference?: string;
    customerDob?: string;
    customerFatherName?: string;
    customerEmail?: string;
    customerAddress?: string;
    customerIsCompany?: number;
    additionalContext?: string;
  };
  // eslint-disable-next-line
  onSubmit?: (formData: any) => void;
  onFeedback?: (message: string, details?: string, success?: boolean) => void;
  onResultsUpdate?: (results: string) => void;
}

const TestingDataForm: React.FC<TestingDataFormProps> = ({
  agentId,
  initialData = {},
  onSubmit,
  onFeedback
}) => {
  const {
    setClientInfo,
    isNifValidated,
    isDobValidated,
    setIsNifValidated,
    setIsDobValidated
  } = useHVAConversation();

  // Estado para los datos del formulario
  const [formData, setFormData] = useState({
    // Sección 1: Información básica
    companyName: initialData.companyName || '',
    customerName: initialData.customerName || '',
    importId: initialData.importId || '',
    recordId: initialData.recordId || '',
    customerPhone: initialData.customerPhone || '',

    // Sección 2: Campos validables
    customerTrn: initialData.customerTrn || '',
    customerReference: initialData.customerReference || '',
    customerDob: initialData.customerDob || '',
    customerFatherName: initialData.customerFatherName || '',
    customerEmail: initialData.customerEmail || '',
    customerAddress: initialData.customerAddress || '',
    customerIsCompany: initialData.customerIsCompany || 0,

    // Sección 3: Contexto adicional
    additionalContext:
      initialData.additionalContext ||
      JSON.stringify(
        {
          callcontext: {},
          bxpcontext: {}
        },
        null,
        2
      )
  });

  // Estado para controlar la validación del JSON
  const [isJsonValid, setIsJsonValid] = useState(true);
  const [jsonError, setJsonError] = useState('');
  const [hasRequiredFields, setHasRequiredFields] = useState(true);

  // Estado para manejar el guardado
  const [isSaving, setIsSaving] = useState(false);

  // Estado para el modal de selección de clientes
  const [showCustomerModal, setShowCustomerModal] = useState(false);

  // Función para validar que el JSON sea correcto
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      setJsonError('');
      return true;
    } catch (error) {
      setJsonError((error as Error).message);
      return false;
    }
  };

  // Validar campos requeridos
  const validateRequiredFields = () => {
    const isNameValid = !!formData.customerName.trim();
    const isTrnValid = !!formData.customerTrn.trim();
    const isImportIdValid = !!formData.importId.trim();
    const isRecordIdValid = !!formData.recordId.trim();
    const isPhoneValid = !!formData.customerPhone.trim();

    setHasRequiredFields(
      isNameValid &&
        isTrnValid &&
        isImportIdValid &&
        isRecordIdValid &&
        isPhoneValid
    );
    return (
      isNameValid &&
      isTrnValid &&
      isImportIdValid &&
      isRecordIdValid &&
      isPhoneValid
    );
  };

  // Manejador para cambios en los inputs
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // Validar el JSON si estamos modificando el contexto adicional
    if (name === 'additionalContext') {
      const isValid = validateJson(value);
      setIsJsonValid(isValid);
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Formatear el JSON del contexto adicional
  const formatJson = () => {
    try {
      const parsedJson = JSON.parse(formData.additionalContext);
      const formattedJson = JSON.stringify(parsedJson, null, 2);
      setFormData(prev => ({
        ...prev,
        additionalContext: formattedJson
      }));
      setIsJsonValid(true);
      setJsonError('');
    } catch (error) {
      setJsonError((error as Error).message);
      setIsJsonValid(false);
    }
  };

  // Actualizar el contexto del cliente cuando cambian los datos validables
  useEffect(() => {
    let callContextData = {};
    let bxpContextData = {};

    try {
      const parsedData = JSON.parse(formData.additionalContext);

      // Extraer callContext y bxpContext del JSON
      if (parsedData.callContext) {
        callContextData = parsedData.callContext;
      } else if (parsedData.callcontext) {
        callContextData = parsedData.callcontext;
      }

      if (parsedData.bxpContext) {
        bxpContextData = parsedData.bxpContext;
      } else if (parsedData.bxpcontext) {
        bxpContextData = parsedData.bxpcontext;
      }

      // Si el JSON es un objeto plano sin subcampos, asumimos que es callContext
      if (
        !parsedData.callContext &&
        !parsedData.callcontext &&
        !parsedData.bxpContext &&
        !parsedData.bxpcontext &&
        typeof parsedData === 'object'
      ) {
        callContextData = parsedData;
      }
    } catch (error) {
      console.error('Error parsing context JSON:', error);
      callContextData = {};
      bxpContextData = {};
    }

    // Crear una fecha de nacimiento válida si está disponible
    let dobDate: Date | undefined;
    if (formData.customerDob) {
      try {
        dobDate = new Date(formData.customerDob);
        if (isNaN(dobDate.getTime())) {
          dobDate = undefined;
        }
      } catch (error) {
        console.error('Error parsing date of birth:', error);
        dobDate = undefined;
      }
    }

    // Actualizar clientInfo con la estructura alineada con la API
    setClientInfo({
      // Campos básicos individuales
      companyname: formData.companyName,
      customername: formData.customerName,
      customertrn: formData.customerTrn,
      customerphone: formData.customerPhone,
      importid: formData.importId,
      recordid: formData.recordId,
      customerreference: formData.customerReference,
      customerdob: dobDate,
      customeraddress: formData.customerAddress,
      customeremail: formData.customerEmail,
      customeriscompany: formData.customerIsCompany,
      customerfathername: formData.customerFatherName,

      // Contexto de llamada como objeto genérico
      callcontext: callContextData,

      // Ahora incluimos bxpcontext
      bxpcontext: bxpContextData

      // No incluimos bxpcontext aquí ya que será manejado externamente
    });

    // Validar campos requeridos
    validateRequiredFields();
  }, [formData, setClientInfo]);

  // Envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar campos requeridos
    if (!validateRequiredFields()) {
      if (onFeedback) {
        onFeedback(
          'Missing required fields',
          'Name, Phone, TRN and Date of Birth are required',
          false
        );
      }
      return;
    }

    // Validar el JSON antes de enviar
    if (!validateJson(formData.additionalContext)) {
      if (onFeedback) {
        onFeedback('Invalid JSON', jsonError, false);
      }
      return;
    }

    setIsSaving(true);

    try {
      // Parsear el contexto adicional
      const contextData = JSON.parse(formData.additionalContext);

      // Preparar datos para guardar en la BD
      const sampleDataToSave = {
        baseSampleData: {
          companyname: formData.companyName,
          customername: formData.customerName,
          customerphone: formData.customerPhone,
          customertrn: formData.customerTrn,
          customerreference: formData.customerReference,
          customerdob: formData.customerDob,
          customerfathername: formData.customerFatherName,
          customeraddress: formData.customerAddress,
          customeremail: formData.customerEmail,
          customeriscompany: formData.customerIsCompany,
          importid: formData.importId,
          recordid: formData.recordId
        },
        callContextSample:
          contextData.callContext || contextData.callcontext || {},
        bxpContextSample: contextData.bxpContext || contextData.bxpcontext || {}
      };

      // Guardar en la base de datos
      await updateAgentSampleData(agentId, sampleDataToSave);

      // Resetear estados de validación
      setIsNifValidated(false);
      setIsDobValidated(false);

      // Crear objeto con todos los datos
      const fullData = {
        ...formData,
        ...contextData
      };

      // Llamar a la función onSubmit proporcionada por el padre
      if (onSubmit) {
        onSubmit(fullData);
      }

      // Proporcionar feedback
      if (onFeedback) {
        onFeedback(
          'Customer information updated',
          `Fullname: ${formData.customerName} | Phone: ${formData.customerPhone} | TRN: ${formData.customerTrn} | DOB: ${formData.customerDob} | ImportID: ${formData.importId} | RecordID: ${formData.recordId}`,
          true
        );
      }
    } catch (error) {
      console.error('Error saving sample data:', error);
      if (onFeedback) {
        onFeedback(
          'Error saving test data',
          error instanceof Error ? error.message : 'Error unknown',
          false
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Manejar selección de cliente desde el modal
  const handleCustomerSelected = (customer: RealCustomer) => {
    try {
      // Parsear contexts si vienen como strings JSON
      let callContextData = {};
      let bxpContextData = {};

      // Parsear callcontext
      if (customer.callcontext) {
        try {
          callContextData =
            typeof customer.callcontext === 'string'
              ? JSON.parse(customer.callcontext)
              : customer.callcontext;
        } catch (e) {
          console.error('Error parsing callcontext:', e);
          callContextData = {};
        }
      }

      // Parsear bxpcontext
      if (customer.bxpcontext) {
        try {
          bxpContextData =
            typeof customer.bxpcontext === 'string'
              ? JSON.parse(customer.bxpcontext)
              : customer.bxpcontext;
        } catch (e) {
          console.error('Error parsing bxpcontext:', e);
          bxpContextData = {};
        }
      }

      // Mapear los datos del cliente al formato del formulario
      const newFormData = {
        companyName: customer.companyname || '',
        customerName: customer.customername || '',
        importId: customer.importid ? customer.importid.toString() : '',
        recordId: customer.recordid ? customer.recordid.toString() : '',
        customerPhone: customer.customerphone || '',
        customerTrn: customer.customertrn || '',
        customerReference: customer.customerreference || '',
        customerDob: customer.customerdob || '',
        customerFatherName: customer.customerfathername || '',
        customerEmail: customer.customeremail || '',
        customerAddress: customer.customeraddress || '',
        customerIsCompany: customer.customeriscompany || 0,
        additionalContext: JSON.stringify(
          {
            callcontext: callContextData,
            bxpcontext: bxpContextData
          },
          null,
          2
        )
      };

      // Actualizar el formulario
      setFormData(newFormData);

      // Validar el JSON
      setIsJsonValid(true);
      setJsonError('');

      // Proporcionar feedback
      if (onFeedback) {
        onFeedback(
          'Real customer data loaded',
          `Customer: ${customer.customername} | Phone: ${customer.customerphone} | TRN: ${customer.customertrn}`,
          true
        );
      }
    } catch (error) {
      console.error('Error loading customer data:', error);
      if (onFeedback) {
        onFeedback(
          'Error loading customer data',
          error instanceof Error ? error.message : 'Unknown error',
          false
        );
      }
    }
  };

  return (
    <>
      <Form onSubmit={handleSubmit}>
        {/* Sección 1: Información básica */}
        <div className="mb-3">
          <h5 className="mb-1">Basic Information</h5>
          <p className="fs-9 lh-sm pb-0 mb-0">
            Configure the test scenario for the agent conversation.
          </p>
          <div className="p-0 mt-1">
            <Form.Group className="mb-3">
              <Form.Floating>
                <Form.Control
                  className="bg-body mb-2"
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  placeholder="Agent company name"
                />
                <label>Company Name</label>
              </Form.Floating>
              <div className="d-flex gap-2 mb-2 align-items-center">
                <Form.Floating className="col-8">
                  <Form.Control
                    className={`bg-body ${
                      !formData.customerName && 'border-danger'
                    }`}
                    type="text"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleInputChange}
                    placeholder="Customer fullname"
                    required
                  />
                  <label>Customer Name *</label>
                </Form.Floating>

                <Form.Check
                  type="switch"
                  id="customerIsCompany"
                  label="Company"
                  checked={formData.customerIsCompany === 1}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      customerIsCompany: e.target.checked ? 1 : 0
                    }))
                  }
                  className="mt-2 mb-0 fs-9"
                />
              </div>
              <div className="d-flex gap-2 mb-2">
                <Form.Floating className="flex-grow-1">
                  <Form.Control
                    className={`bg-body ${
                      !formData.customerPhone && 'border-danger'
                    }`}
                    type="tel"
                    name="customerPhone"
                    value={formData.customerPhone}
                    onChange={handleInputChange}
                    placeholder="Ej., +34600123456"
                    required
                  />
                  <label>Phone Number *</label>
                </Form.Floating>
                <Form.Floating className="flex-grow-1">
                  <Form.Control
                    className={`bg-body ${
                      !formData.importId && 'border-danger'
                    }`}
                    type="text"
                    name="importId"
                    value={formData.importId}
                    onChange={handleInputChange}
                    placeholder="Ej., IMP20240301"
                    required
                  />
                  <label>Import ID *</label>
                </Form.Floating>

                <Form.Floating className="flex-grow-1">
                  <Form.Control
                    className={`bg-body ${
                      !formData.recordId && 'border-danger'
                    }`}
                    type="text"
                    name="recordId"
                    value={formData.recordId}
                    onChange={handleInputChange}
                    placeholder="Ej., REC12345678"
                    required
                  />
                  <label>Record ID *</label>
                </Form.Floating>
              </div>
            </Form.Group>
          </div>
        </div>

        {/* Sección 2: Campos validables */}
        <div className="mb-3">
          <h5 className="mb-2">Validation Fields</h5>
          <div className="p-0">
            <Form.Group className="mb-3">
              <div className="d-flex gap-2 mb-2">
                <Form.Floating className="flex-grow-1">
                  <Form.Control
                    className={`bg-body ${
                      !formData.customerTrn && 'border-danger'
                    }`}
                    type="text"
                    name="customerTrn"
                    value={formData.customerTrn}
                    onChange={handleInputChange}
                    placeholder="Ej., 12345678Z"
                    isValid={isNifValidated}
                    required
                  />
                  <label>TRN (Tax Reference Number) *</label>
                </Form.Floating>

                <Form.Floating className="flex-grow-1">
                  <Form.Control
                    className="bg-body"
                    type="text"
                    name="customerReference"
                    value={formData.customerReference}
                    onChange={handleInputChange}
                    placeholder="Ej., ACC123456"
                  />
                  <label>Customer Reference</label>
                </Form.Floating>
              </div>

              <div className="d-flex gap-2 mb-2">
                <Form.Floating className="flex-grow-1">
                  <Form.Control
                    className={`bg-body`}
                    type="date"
                    name="customerDob"
                    value={formData.customerDob}
                    onChange={handleInputChange}
                    placeholder="Fecha de nacimiento"
                    isValid={isDobValidated}
                    required
                  />
                  <label>Date of Birth</label>
                </Form.Floating>
                <Form.Floating className="flex-grow-1 col-7">
                  <Form.Control
                    className="bg-body"
                    type="text"
                    name="customerFatherName"
                    value={formData.customerFatherName}
                    onChange={handleInputChange}
                    placeholder="Father's full name"
                  />
                  <label>Father's Name</label>
                </Form.Floating>
              </div>

              <Form.Floating className="flex-grow-1 mb-2">
                <Form.Control
                  className="bg-body"
                  type="email"
                  name="customerEmail"
                  value={formData.customerEmail}
                  onChange={handleInputChange}
                  placeholder="cliente@ejemplo.com"
                />
                <label>Email</label>
              </Form.Floating>

              <Form.Floating>
                <Form.Control
                  className="scrollbar bg-body"
                  as="textarea"
                  style={{ height: '60px' }}
                  name="customerAddress"
                  value={formData.customerAddress}
                  onChange={handleInputChange}
                  placeholder="Dirección completa"
                />
                <label>Address</label>
              </Form.Floating>
            </Form.Group>
          </div>
        </div>

        {/* Sección 3: Contexto adicional (JSON) */}
        <div className="mb-2">
          <div className="d-flex justify-content-between align-items-end mb-2">
            <div>
              <h5 className="mb-1">Additional Context (JSON)</h5>
              <p className="text-muted fs-9 lh-1 pb-0 mb-0">
                Include any additional information needed for the conversation
              </p>
            </div>
            <Button
              variant="link"
              size="sm"
              onClick={formatJson}
              title="Formatear JSON"
              className="p-0 me-2"
            >
              <FontAwesomeIcon icon={faCheck} /> Check & Beautify
            </Button>
          </div>
          <div className="p-0">
            <Form.Group className="mb-2 pt-0">
              <Form.Control
                as="textarea"
                rows={15}
                name="additionalContext"
                value={formData.additionalContext}
                onChange={handleInputChange}
                placeholder="Ingresa datos de contexto adicionales en formato JSON"
                className={`scrollbar bg-body shadow-none lh-sm ${
                  !isJsonValid ? 'is-invalid' : ''
                }`}
              />
              {!isJsonValid && (
                <Form.Control.Feedback type="invalid">
                  {jsonError}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </div>
        </div>

        {!hasRequiredFields && (
          <div className="bg-warning-subtle rounded p-3 mb-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            <small>
              Se requieren los campos marcados con * para iniciar una llamada
            </small>
          </div>
        )}

        <div className="d-flex justify-content-end mb-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowCustomerModal(true)}
            className="me-2"
          >
            <FontAwesomeIcon icon={faDatabase} className="me-2" />
            Load from Database
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            className="px-4"
            disabled={!isJsonValid || !hasRequiredFields || isSaving}
          >
            {isSaving ? (
              <>
                <FontAwesomeIcon icon={faC} className="me-1" spin />
                Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPen} className="me-1" />
                Update Test Data
              </>
            )}
          </Button>
        </div>
      </Form>

      {/* Modal de selección de clientes */}
      <HVACustomerSelectorModal
        show={showCustomerModal}
        onHide={() => setShowCustomerModal(false)}
        onSelectCustomer={handleCustomerSelected}
      />
    </>
  );
};

export default TestingDataForm;
